function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART13: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 151. NPS Detractor Recovery (HubSpot, Pattern G) ──────────────────

  "NPS Detractor Recovery": {
    nodes: [
      trigger(
        "HubSpot webhook: NPS survey response received with score 0-6",
        "webhook_app",
        {
          source: "hubspot",
          event: "nps_survey_response",
          howItStarts:
            "Fires when a HubSpot NPS survey response is submitted with a detractor score (0-6), indicating the customer is unhappy and needs immediate recovery outreach.",
          setup:
            "Connect HubSpot and configure the NPS survey response webhook. This workflow filters for detractor scores only (0-6).",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch contact profile, company, and recent tickets",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?associations=companies,tickets&properties=email,firstname,lastname,hs_object_id,lifecyclestage,hs_lead_status,num_associated_deals",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "properties.num_associated_deals",
                customName: "deal_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze NPS feedback and categorize detractor reason",
        520,
        "You are a customer experience analyst. Given an NPS detractor score and any verbatim feedback, categorize the reason into one of: product_issue, support_issue, pricing_concern, missing_feature, onboarding_gap, or other. Return reason_category, severity (critical/high/medium), recommended_action, and empathetic_opening_line for outreach.",
        "NPS score: {nps_score}. Feedback: {nps_feedback}. Customer: {first_name} {last_name}. Lifecycle: {lifecycle_stage}. Deals: {deal_count}. Categorize detractor reason and recommend recovery action.",
        [
          { field: "reason_category", as: "reason_category", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "empathetic_opening",
            as: "empathetic_opening",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Is severity critical (score 0-3 or open tickets)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "severity", operator: "equals", value: "critical" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Personal recovery outreach from CS lead with action plan",
        1000,
        "{contact_email}",
        "We hear you, {first_name} — let's make this right",
        "Hi {first_name},\\n\\n{empathetic_opening}\\n\\nI personally reviewed your feedback and want to address this directly. Based on what you shared, here is what we are doing:\\n\\n{recommended_action}\\n\\nI would love to schedule a 15-minute call to discuss further. You can book directly here: {meeting_link}\\n\\nYour experience matters to us — we will not let this slide.\\n\\nBest,\\n{cs_lead_name}\\nCustomer Success Lead",
      ),
      n("6", "delayAction", "Wait 5 days for customer response", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Has customer responded or booked a meeting?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "meeting_booked", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Escalate unresponsive detractor to CSM manager",
        1720,
        "🚨 NPS Detractor Escalation — {first_name} {last_name} ({contact_email}) scored {nps_score}. Reason: {reason_category} ({severity}). No response after 5 days. Recommended: {recommended_action}. Lifecycle: {lifecycle_stage}. Deals: {deal_count}. Needs immediate CSM manager intervention.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "NPS detractor recovery workflow for HubSpot. Triggers on NPS survey responses with scores 0-6, fetches the contact profile with company and ticket associations, uses AI to categorize the detractor reason (product issue, support, pricing, etc.) and severity level, sends a personal empathetic recovery email from the CS lead with a concrete action plan and meeting link, waits 5 days, then escalates unresponsive detractors to the CSM manager via Slack for immediate intervention.",
  },

  // ─── 152. Customer Advocacy Ask (HubSpot, Pattern J) ───────────────────

  "Customer Advocacy Ask": {
    nodes: [
      trigger(
        "HubSpot webhook: NPS promoter score 9-10 received",
        "webhook_app",
        {
          source: "hubspot",
          event: "nps_survey_response",
          howItStarts:
            "Fires when a HubSpot NPS survey response comes in with a promoter score (9-10), signaling a happy customer who may be willing to advocate for the brand.",
          setup:
            "Connect HubSpot and configure the NPS survey webhook. This workflow filters for promoter scores (9-10) only.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch contact profile, deal history, and advocacy status",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,lifecyclestage,hs_lead_status,last_advocacy_ask_date,advocacy_status,num_associated_deals,recent_deal_amount",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "properties.last_advocacy_ask_date",
                customName: "last_ask_date",
                type: "string",
              },
              {
                name: "properties.advocacy_status",
                customName: "advocacy_status",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Eligible for advocacy ask (no ask in last 90 days)?",
        520,
        {
          conditionDetails: {
            conditions: [
              {
                field: "advocacy_status",
                operator: "not_equals",
                value: "recently_asked",
              },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Email: Personalized advocacy ask with case study or referral option",
        760,
        "{contact_email}",
        "{first_name}, your success story could inspire others",
        "Hi {first_name},\\n\\nThank you for your incredible feedback — a score of {nps_score} means the world to us!\\n\\nWe would love to share your success story with others who are facing similar challenges. There are a few ways you could help:\\n\\n1. **Quick testimonial** — A 2-3 sentence quote we can feature (takes 2 minutes)\\n2. **Case study** — A short interview about your results (15 minutes)\\n3. **Referral** — Know someone who could benefit? We will thank you both with a $50 gift card\\n\\nJust reply with the number that interests you, or click here to schedule a quick chat: {meeting_link}\\n\\nEither way, thanks for being an amazing customer!\\n\\nBest,\\n{csm_name}",
      ),
      n("5", "delayAction", "Wait 6 days for response", 1000, {
        delayDetails: { duration: 6, unit: "days" },
      }),
      n("6", "condition", "Has customer accepted an advocacy option?", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "advocacy_response",
              operator: "equals",
              value: "accepted",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Gentle reminder with simplified one-click testimonial option",
        1480,
        "{contact_email}",
        "Quick follow-up: one-click way to share your story, {first_name}",
        "Hi {first_name},\\n\\nI know you are busy, so I wanted to make this as easy as possible. Just click the link below to submit a quick testimonial — it takes less than 60 seconds:\\n\\n{testimonial_form_link}\\n\\nAlternatively, if you know someone who could benefit from our platform, just forward them this link: {referral_link}\\n\\nThank you for being part of our community!\\n\\n{csm_name}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log advocacy ask outcome and response status",
        1720,
        "Advocacy_Tracking",
        '[["{new Date().toISOString()}","{contact_email}","{first_name}","{nps_score}","{lifecycle_stage}","{advocacy_status}","{advocacy_response}","hubspot"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Customer advocacy workflow for HubSpot NPS promoters. Triggers on NPS scores of 9-10, fetches the contact profile to check advocacy eligibility (not asked in last 90 days), sends a personalized email with three advocacy options (testimonial, case study, referral), waits 6 days, then sends a simplified reminder with a one-click testimonial form for non-responders. Logs all advocacy ask outcomes to Google Sheets for tracking conversion rates.",
  },

  // ─── 153. Re-Engagement / Sunset Workflow (HubSpot, Pattern G) ─────────

  "Re-Engagement / Sunset Workflow": {
    nodes: [
      trigger(
        "Schedule: Daily scan for HubSpot contacts inactive 90+ days",
        "schedule",
        {
          source: "hubspot",
          schedule: "Every day at 9:00 AM UTC",
          howItStarts:
            "Runs daily to identify HubSpot contacts who have not opened an email or visited the site in 90+ days, initiating a re-engagement sequence before suppressing them.",
          setup:
            "Configure the schedule trigger and connect HubSpot. The workflow queries for contacts with last activity older than 90 days.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch inactive contacts with last activity and purchase history",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"hs_email_last_open_date","operator":"LT","value":"{ninety_days_ago_ms}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","firstname","lastname","hs_email_last_open_date","hs_analytics_last_visit_date","total_revenue","lifecyclestage"]',
              },
              { key: "limit", value: "100" },
            ],
            variables: [
              {
                name: "results",
                customName: "inactive_contacts",
                type: "object",
              },
              { name: "total", customName: "total_inactive", type: "number" },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze inactivity pattern and craft re-engagement angle",
        520,
        "You are a retention marketing strategist. Given a contacts last activity date, purchase history, and lifecycle stage, determine the best re-engagement angle. Return re_engage_angle (new_features, content_roundup, exclusive_offer, we_miss_you), subject_line, and sunset_risk (high/medium/low).",
        "Contact: {first_name}. Last email open: {hs_email_last_open_date}. Last site visit: {hs_analytics_last_visit_date}. Total revenue: {total_revenue}. Lifecycle: {lifecyclestage}. Determine the best re-engagement strategy.",
        [
          { field: "re_engage_angle", as: "re_engage_angle", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "sunset_risk", as: "sunset_risk", type: "string" },
        ],
      ),
      n(
        "4",
        "condition",
        "Is this a high-value contact (total revenue > $500)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "total_revenue",
                operator: "greater_than",
                value: "500",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Re-engagement with personalized angle and preference center link",
        1000,
        "{contact_email}",
        "{subject_line}",
        "Hi {first_name},\\n\\nIt has been a while since we connected, and a lot has changed! Here is what you have been missing:\\n\\n• New features that simplify your workflow\\n• Updated resources tailored to your needs\\n• An exclusive offer just for returning members\\n\\nWe would love to have you back. Click below to update your preferences and choose what matters most to you:\\n\\n{preference_center_link}\\n\\nIf you no longer wish to hear from us, we understand — you can unsubscribe at any time.\\n\\nWe hope to see you soon,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 10 days for re-engagement activity", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n("7", "condition", "Has contact re-engaged (opened or clicked)?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "re_engaged", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "8",
        "hitApi",
        "HubSpot: Suppress unengaged contact and update marketing status",
        1720,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hs_marketable_status":"false","sunset_date":"{today_date}","sunset_reason":"90_day_inactivity_no_reengagement"}',
              },
            ],
            variables: [
              {
                name: "id",
                customName: "suppressed_contact_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Re-engagement and sunset workflow for HubSpot. Runs daily to find contacts inactive for 90+ days, uses AI to determine the best re-engagement angle (new features, exclusive offer, content roundup) based on purchase history and lifecycle stage, sends a personalized re-engagement email with a preference center link, waits 10 days, then suppresses contacts who still have not re-engaged by updating their HubSpot marketable status to false with a sunset reason.",
  },

  // ─── 154. Free Trial Onboarding (HubSpot, Pattern A) ──────────────────

  "Free Trial Onboarding": {
    nodes: [
      trigger("HubSpot webhook: Free trial signup detected", "webhook_app", {
        source: "hubspot",
        event: "contact.propertyChange",
        howItStarts:
          "Fires when a HubSpot contact property changes to indicate a free trial has started, such as lifecycle stage moving to 'Free Trial' or a trial_start_date being set.",
        setup:
          "Connect HubSpot and configure a property change webhook for the trial_start_date or lifecyclestage property. Filter for new trial activations.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract trial user details and determine onboarding priority",
        280,
        "You are an onboarding strategist. Given a new trial users profile (company size, industry, role), determine onboarding_priority (high/standard), recommended_first_action (the single most impactful setup step), and personalized_welcome_hook (one sentence connecting their industry to product value).",
        "Trial user: {first_name} {last_name}. Email: {contact_email}. Company: {company_name}. Industry: {industry}. Role: {job_title}. Company size: {company_size}. Determine onboarding priority and first action.",
        [
          {
            field: "onboarding_priority",
            as: "onboarding_priority",
            type: "string",
          },
          {
            field: "recommended_first_action",
            as: "recommended_first_action",
            type: "string",
          },
          {
            field: "personalized_welcome_hook",
            as: "personalized_welcome_hook",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch contact profile and trial details",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,trial_start_date,trial_end_date,hs_analytics_source",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.trial_end_date",
                customName: "trial_end_date",
                type: "string",
              },
              {
                name: "properties.hs_analytics_source",
                customName: "signup_source",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is this an existing customer starting a new product trial?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "lifecycle_stage",
                operator: "equals",
                value: "customer",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Welcome to your trial with personalized first step",
        1000,
        "{contact_email}",
        "Welcome to your free trial, {first_name} — here's your first step",
        "Hi {first_name},\\n\\n{personalized_welcome_hook}\\n\\nYour free trial is active and you have 14 days to explore everything. Here is the single most impactful thing you can do right now:\\n\\n**{recommended_first_action}**\\n\\nClick here to get started: {onboarding_link}\\n\\nYour trial dashboard shows your progress — aim to complete 3 key actions this week for the best experience.\\n\\nNeed help? Reply to this email or book a quick orientation: {meeting_link}\\n\\nLet's make the most of your trial!\\n\\n{onboarding_team}",
      ),
      n("6", "delayAction", "Wait 2 days for initial activation", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n("7", "condition", "Has user completed first key action?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "first_action_completed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Activation nudge with quick-start video and support offer",
        1720,
        "{contact_email}",
        "{first_name}, don't miss out — complete your setup in 5 minutes",
        "Hi {first_name},\\n\\nI noticed you have not had a chance to complete your setup yet — no worries, it only takes about 5 minutes!\\n\\nHere is a quick-start video that walks you through it step by step: {quickstart_video_link}\\n\\nOr if you prefer hands-on help, our onboarding specialist can walk you through it live: {book_call_link}\\n\\nYour trial has {days_remaining} days left — getting set up now means you will have plenty of time to see real results.\\n\\n{onboarding_team}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Free trial onboarding workflow for HubSpot. Triggers on trial signup, uses AI to determine onboarding priority and the single most impactful first action based on company profile and industry, fetches full contact and trial details from HubSpot, sends a personalized welcome email with a clear first step and onboarding link, waits 2 days, then sends an activation nudge with a quick-start video to users who have not completed their first key action.",
  },

  // ─── 155. Free User to Sales Prompt (HubSpot, Pattern L) ──────────────

  "Free User to Sales Prompt": {
    nodes: [
      trigger(
        "HubSpot webhook: Free plan user approaching usage limit",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a HubSpot contact on the free plan hits 80% or more of their usage limit (contacts, emails sent, features used), indicating they may be ready for an upgrade conversation.",
          setup:
            "Connect HubSpot and configure a property change webhook for the usage_percentage or plan_limit_reached property. Filter for free plan users at 80%+ usage.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch contact profile, usage data, and company details",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?associations=companies&properties=email,firstname,lastname,jobtitle,company,plan_type,usage_percentage,feature_usage_score,trial_end_date,lifecyclestage",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.usage_percentage",
                customName: "usage_percentage",
                type: "number",
              },
              {
                name: "properties.feature_usage_score",
                customName: "feature_usage_score",
                type: "number",
              },
              {
                name: "properties.plan_type",
                customName: "plan_type",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze usage pattern and recommend upgrade path",
        520,
        "You are a product-led growth analyst. Given a free users usage metrics, feature adoption, and company profile, determine upgrade_readiness (hot/warm/cool), best_plan_recommendation (starter/professional/enterprise), key_value_driver (the feature or limit that matters most to this user), and personalized_upgrade_pitch (one compelling sentence).",
        "User: {first_name}. Plan: {plan_type}. Usage: {usage_percentage}%. Feature score: {feature_usage_score}. Company: {company_name}. Role: {job_title}. Analyze upgrade readiness and recommend the best plan.",
        [
          {
            field: "upgrade_readiness",
            as: "upgrade_readiness",
            type: "string",
          },
          {
            field: "best_plan_recommendation",
            as: "best_plan",
            type: "string",
          },
          { field: "key_value_driver", as: "key_value_driver", type: "string" },
          {
            field: "personalized_upgrade_pitch",
            as: "upgrade_pitch",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Is upgrade readiness hot (usage > 90% or high feature adoption)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "upgrade_readiness", operator: "equals", value: "hot" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Personalized upgrade prompt with value-based pitch",
        1000,
        "{contact_email}",
        "{first_name}, you're getting great results — unlock even more",
        "Hi {first_name},\\n\\n{upgrade_pitch}\\n\\nYou are currently using {usage_percentage}% of your free plan, which tells us you are getting real value. Here is what upgrading to {best_plan} unlocks for you:\\n\\n• **{key_value_driver}** — the feature that matters most to your workflow\\n• Higher limits so you never hit a wall\\n• Priority support for faster answers\\n\\nSee the full comparison: {pricing_page_link}\\n\\nOr if you would like a personalized walkthrough of what {best_plan} means for your team, book a quick call: {sales_meeting_link}\\n\\nBest,\\n{growth_team}",
      ),
      n("6", "delayAction", "Wait 5 days for upgrade or engagement", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Has user upgraded or booked a sales call?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "upgraded", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Alert sales team about high-intent free user for outreach",
        1720,
        "🎯 Sales Assist — Free user {first_name} ({contact_email}) at {usage_percentage}% usage on free plan. Upgrade readiness: {upgrade_readiness}. Recommended plan: {best_plan}. Key driver: {key_value_driver}. Feature score: {feature_usage_score}. No self-serve upgrade after 5 days — needs sales outreach.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Free user to sales prompt workflow for HubSpot product-led growth. Triggers when a free plan user hits 80%+ usage limits, fetches contact and usage data, uses AI to assess upgrade readiness and recommend the best plan with a personalized value pitch, sends an upgrade email highlighting the key value driver and pricing comparison, waits 5 days, then alerts the sales team via Slack about high-intent free users who have not self-served an upgrade for direct outreach.",
  },

  // ─── 156. Closed-Won Handoff to CSM (HubSpot, Pattern E) ──────────────

  "Closed-Won Handoff to CSM": {
    nodes: [
      trigger(
        "HubSpot webhook: Deal stage changed to Closed Won",
        "webhook_app",
        {
          source: "hubspot",
          event: "deal.propertyChange",
          howItStarts:
            "Fires when a HubSpot deal stage changes to 'closedwon', initiating the handoff from sales to customer success for onboarding.",
          setup:
            "Connect HubSpot and configure the deal property change webhook for the dealstage property. Filter for transitions to 'closedwon'.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract deal details and generate onboarding brief",
        280,
        "You are a customer success handoff specialist. Given deal details (products, amount, close date, sales notes), generate a structured onboarding_brief with key_objectives (array), implementation_timeline, risk_factors (array), and first_30_day_milestones (array).",
        "Deal: {deal_name}. Amount: {deal_amount}. Products: {line_items}. Close date: {close_date}. Sales notes: {deal_notes}. Contact: {first_name} {last_name} at {company_name}. Generate an onboarding brief for the CSM.",
        [
          { field: "key_objectives", as: "key_objectives", type: "object" },
          {
            field: "implementation_timeline",
            as: "implementation_timeline",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "object" },
          {
            field: "first_30_day_milestones",
            as: "milestones",
            type: "object",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch deal with associated contacts and line items",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?associations=contacts,line_items&properties=dealname,amount,dealstage,closedate,hubspot_owner_id,deal_currency_code,hs_priority,notes_last_updated",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.dealname",
                customName: "deal_name",
                type: "string",
              },
              {
                name: "properties.amount",
                customName: "deal_amount",
                type: "number",
              },
              {
                name: "properties.hubspot_owner_id",
                customName: "sales_owner_id",
                type: "string",
              },
              {
                name: "properties.closedate",
                customName: "close_date",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is deal value above $10,000 (high-touch onboarding)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "deal_amount",
                operator: "greater_than",
                value: "10000",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: CSM introduction with onboarding timeline and kickoff invite",
        1000,
        "{contact_email}",
        "Welcome aboard, {first_name} — meet your Customer Success Manager",
        "Hi {first_name},\\n\\nCongratulations on getting started! I am {csm_name}, your dedicated Customer Success Manager, and I will be your main point of contact from here.\\n\\nHere is what to expect in the next 30 days:\\n\\n1. **Kickoff call** (this week) — We will align on your goals and map out your implementation\\n2. **Technical setup** — Our team handles the heavy lifting\\n3. **First milestone** — You will see initial results within 2 weeks\\n\\nI have already reviewed your objectives with {sales_rep_name} to ensure a seamless transition. Book your kickoff call here: {kickoff_meeting_link}\\n\\nImplementation timeline: {implementation_timeline}\\n\\nLooking forward to partnering with you!\\n\\n{csm_name}\\nCustomer Success Manager",
      ),
      n("6", "delayAction", "Wait 7 days for onboarding progress", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Has kickoff call been completed?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "kickoff_completed", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Onboarding check-in with milestone tracker and resource links",
        1720,
        "{contact_email}",
        "Quick check-in: how's your onboarding going, {first_name}?",
        "Hi {first_name},\\n\\nIt has been a week since we kicked things off, and I wanted to check in on your progress.\\n\\nHere are your first 30-day milestones:\\n{milestones_formatted}\\n\\nIf you have not had a chance to book your kickoff call yet, grab a time here: {kickoff_meeting_link}\\n\\nHelpful resources to get moving:\\n• Getting Started Guide: {getting_started_link}\\n• Video Tutorials: {tutorial_link}\\n• Help Center: {help_center_link}\\n\\nI am here whenever you need me — just reply to this email.\\n\\n{csm_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Closed-won deal handoff to CSM workflow for HubSpot. Triggers when a deal moves to Closed Won, uses AI to generate a structured onboarding brief with objectives and 30-day milestones from deal details, fetches deal associations and line items, checks deal value for high-touch routing, sends a CSM introduction email with implementation timeline and kickoff booking link, waits 7 days, then follows up with a milestone check-in and resource links for customers who have not completed their kickoff.",
  },

  // ─── 157. Referral Request After Positive Milestone (HubSpot, Pattern J) ───

  "Referral Request After Positive Milestone": {
    nodes: [
      trigger(
        "HubSpot webhook: Customer achieves positive milestone event",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a HubSpot contact reaches a positive milestone — such as ROI target achieved, usage anniversary, successful project completion, or health score reaching 'excellent' — making it an ideal time for a referral ask.",
          setup:
            "Connect HubSpot and configure a property change webhook for milestone properties (health_score, roi_achieved, usage_anniversary). Filter for positive milestone events.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch contact profile, health score, and referral history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,company,health_score,last_referral_ask_date,referral_count,customer_since,total_revenue,lifecyclestage",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.health_score",
                customName: "health_score",
                type: "string",
              },
              {
                name: "properties.last_referral_ask_date",
                customName: "last_referral_ask",
                type: "string",
              },
              {
                name: "properties.referral_count",
                customName: "referral_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Eligible for referral ask (not asked in last 60 days)?",
        520,
        {
          conditionDetails: {
            conditions: [
              {
                field: "last_referral_ask",
                operator: "not_equals",
                value: "recent",
              },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Email: Milestone celebration with embedded referral ask and incentive",
        760,
        "{contact_email}",
        "Congrats on the milestone, {first_name}! Here's a way to share the love",
        "Hi {first_name},\\n\\nWe are thrilled to see you hit this milestone — {milestone_description}! Moments like these remind us why we do what we do.\\n\\nSince things are going well, we have a favor to ask: do you know anyone who could benefit from similar results?\\n\\nWhen you refer a colleague:\\n• **They get** a 20% discount on their first 3 months\\n• **You get** a $100 credit applied to your next invoice\\n\\nShare your unique referral link: {referral_link}\\n\\nOr simply forward this email — we will handle the rest.\\n\\nThanks for being an amazing customer!\\n\\n{csm_name}",
      ),
      n("5", "delayAction", "Wait 7 days for referral action", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n(
        "6",
        "condition",
        "Has customer shared referral link or referred someone?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { field: "referral_shared", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Email: Gentle referral reminder with alternative peer introduction offer",
        1480,
        "{contact_email}",
        "Quick reminder: your referral link is ready, {first_name}",
        "Hi {first_name},\\n\\nJust a quick follow-up on the referral opportunity. If sharing a link is not your style, no problem — here are two other easy ways:\\n\\n1. **Reply with a name and email** — I will reach out personally and mention you\\n2. **Forward this email** to a colleague with a quick intro\\n\\nYour referral link (still active): {referral_link}\\n\\nRemember, you both benefit — $100 credit for you, 20% off for them.\\n\\nThanks for considering it!\\n\\n{csm_name}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log referral ask outcome and milestone context",
        1720,
        "Referral_Tracking",
        '[["{new Date().toISOString()}","{contact_email}","{first_name}","{milestone_type}","{health_score}","{referral_count}","{referral_shared}","{total_revenue}","hubspot"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Referral request workflow triggered by positive customer milestones in HubSpot. Fetches contact profile with health score and referral history, checks eligibility (not asked in last 60 days), sends a milestone celebration email with an embedded referral ask and double-sided incentive ($100 credit for referrer, 20% off for referee), waits 7 days, then sends a gentle reminder with alternative referral methods for non-responders. Logs all referral outcomes to Google Sheets.",
  },

  // ─── 158. Welcome Journey (Mailchimp, Pattern A) ──────────────────────

  "Welcome Journey": {
    nodes: [
      trigger(
        "Mailchimp webhook: New subscriber added to audience",
        "webhook_app",
        {
          source: "mailchimp",
          event: "subscribe",
          howItStarts:
            "Fires when a new subscriber is added to a Mailchimp audience list via signup form, API, or import, kicking off the welcome journey to convert them into a first-time buyer.",
          setup:
            "Connect Mailchimp and configure the subscribe webhook for your primary audience. Ensure double opt-in is enabled.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract subscriber details and determine welcome segment",
        280,
        "You are a welcome journey strategist. Given a new subscribers signup source, location, and any tags, determine welcome_segment (new_shopper, returning_visitor, referral, organic), recommended_content_theme (bestsellers, new_arrivals, category_match, brand_story), and personalized_hook (one engaging sentence to start the welcome email).",
        "Subscriber email: {email}. Signup source: {signup_source}. Tags: {tags}. Location: {location}. Merge fields: {merge_fields}. Determine welcome segment and content theme.",
        [
          { field: "welcome_segment", as: "welcome_segment", type: "string" },
          {
            field: "recommended_content_theme",
            as: "content_theme",
            type: "string",
          },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber profile and audience membership details",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{dc}.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "email_address",
                customName: "subscriber_email",
                type: "string",
              },
              {
                name: "merge_fields.FNAME",
                customName: "first_name",
                type: "string",
              },
              { name: "source", customName: "signup_source", type: "string" },
              { name: "tags_count", customName: "tags_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is subscriber a returning visitor (previously on list)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "welcome_segment",
                operator: "equals",
                value: "returning_visitor",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Welcome with brand story, bestsellers, and first purchase incentive",
        1000,
        "{subscriber_email}",
        "Welcome to the family, {first_name}! Here's 15% off your first order",
        "Hi {first_name},\\n\\n{personalized_hook}\\n\\nWe are so glad you are here! Here is a little about who we are and why customers love us:\\n\\n🛍 **Curated Quality** — Every product is handpicked and tested\\n⚡ **Fast Shipping** — Free shipping on orders over $50\\n💝 **Happiness Guaranteed** — 30-day no-questions-asked returns\\n\\nTo celebrate you joining, here is 15% off your first order:\\n\\n**Use code: WELCOME15**\\n\\nShop our bestsellers: {bestsellers_link}\\nOr explore new arrivals: {new_arrivals_link}\\n\\nThis code expires in 7 days — don't wait too long!\\n\\nWelcome aboard,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 2 days for first engagement", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n("7", "condition", "Has subscriber made a first purchase?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "first_purchase_made", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: First purchase nudge with category highlights and social proof",
        1720,
        "{subscriber_email}",
        "{first_name}, your WELCOME15 code expires soon — here's what's trending",
        'Hi {first_name},\\n\\nJust a friendly reminder — your 15% welcome discount (WELCOME15) expires in 5 days!\\n\\nHere is what other new customers are loving right now:\\n\\n⭐ {trending_product_1} — "{review_snippet_1}"\\n⭐ {trending_product_2} — "{review_snippet_2}"\\n⭐ {trending_product_3} — "{review_snippet_3}"\\n\\nShop now with your discount: {shop_link}?coupon=WELCOME15\\n\\nFree shipping on orders over $50!\\n\\n— The Team',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Welcome journey for new Mailchimp subscribers. Triggers on new audience subscription, uses AI to determine the welcome segment and best content theme based on signup source, fetches full subscriber profile, sends a branded welcome email with a 15% first-order discount code (WELCOME15), brand story, and curated product links, waits 2 days, then sends a purchase nudge with trending products and social proof to subscribers who have not yet converted.",
  },

  // ─── 159. Welcome Discount Reminder (Mailchimp, Pattern A) ────────────

  "Welcome Discount Reminder": {
    nodes: [
      trigger(
        "Mailchimp webhook: New subscriber with welcome discount assigned",
        "webhook_app",
        {
          source: "mailchimp",
          event: "subscribe",
          howItStarts:
            "Fires when a new subscriber joins a Mailchimp audience and is tagged with a welcome discount code, starting a reminder sequence to drive first purchase before the code expires.",
          setup:
            "Connect Mailchimp and configure the subscribe webhook. Ensure new subscribers are auto-tagged with a welcome discount code and expiry date.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract subscriber profile and craft urgency messaging",
        280,
        "You are an e-commerce conversion copywriter. Given a new subscriber with a welcome discount, determine days_until_expiry, urgency_level (low/medium/high), recommended_product_category (based on signup source or browse data), and urgency_subject_line (compelling subject line).",
        "Subscriber: {first_name}. Email: {email}. Discount code: {discount_code}. Discount value: {discount_value}. Expiry date: {discount_expiry}. Signup source: {signup_source}. Determine urgency and recommended products.",
        [
          {
            field: "days_until_expiry",
            as: "days_until_expiry",
            type: "number",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "recommended_product_category",
            as: "recommended_category",
            type: "string",
          },
          {
            field: "urgency_subject_line",
            as: "urgency_subject",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Mailchimp: Fetch subscriber details and tags", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{dc}.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
          headers: [
            { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "email_address",
              customName: "subscriber_email",
              type: "string",
            },
            {
              name: "merge_fields.FNAME",
              customName: "first_name",
              type: "string",
            },
            { name: "tags", customName: "subscriber_tags", type: "object" },
            {
              name: "status",
              customName: "subscription_status",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Is discount about to expire (3 days or less)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "days_until_expiry", operator: "less_than", value: "4" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Welcome discount reminder with curated product picks",
        1000,
        "{subscriber_email}",
        "{first_name}, your {discount_value} discount is waiting — don't let it slip away",
        "Hi {first_name},\\n\\nWelcome to our community! We noticed you have not used your {discount_value} welcome discount yet.\\n\\nHere is your code: **{discount_code}**\\n\\nBased on what shoppers like you love, check out these picks:\\n\\n🔥 {product_1_name} — ${product_1_price}\\n🔥 {product_2_name} — ${product_2_price}\\n🔥 {product_3_name} — ${product_3_price}\\n\\nYour discount expires on {discount_expiry} — use code {discount_code} at checkout.\\n\\nShop now: {shop_link}\\n\\n— The Team",
      ),
      n("6", "delayAction", "Wait 3 days before final reminder", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Has subscriber redeemed the discount?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "discount_redeemed", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Last chance urgency reminder — discount expires tomorrow",
        1720,
        "{subscriber_email}",
        "⏰ Last chance, {first_name} — your {discount_value} discount expires tomorrow",
        "Hi {first_name},\\n\\nThis is your final reminder — your {discount_value} welcome discount expires tomorrow!\\n\\n**Code: {discount_code}**\\n\\nHere is what you could save on our most popular items:\\n\\n✨ {top_seller_1} — was ${original_price_1}, now ${discounted_price_1} with your code\\n✨ {top_seller_2} — was ${original_price_2}, now ${discounted_price_2} with your code\\n\\nDon't miss out — shop now before midnight: {shop_link}?coupon={discount_code}\\n\\nFree shipping on all orders over $50!\\n\\n— The Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Welcome discount reminder journey for Mailchimp. Triggers when a new subscriber is tagged with a welcome discount code, uses AI to assess urgency level and recommend products based on signup source, fetches subscriber details and tags, sends a discount reminder email with curated product picks and expiry countdown, waits 3 days, then sends a final last-chance urgency email with calculated savings to subscribers who have not redeemed their code.",
  },

  // ─── 160. Abandoned Cart Series (Mailchimp, Pattern B) ────────────────

  "Abandoned Cart Series": {
    nodes: [
      trigger(
        "Mailchimp webhook: Cart abandoned by subscriber",
        "webhook_app",
        {
          source: "mailchimp",
          event: "cart.abandoned",
          howItStarts:
            "Fires when a Mailchimp-connected store detects a cart abandonment event — the subscriber added items to cart but did not complete checkout within the configured abandonment window.",
          setup:
            "Connect Mailchimp with your e-commerce store (Shopify, WooCommerce, etc.) and enable cart abandonment tracking. Configure the abandonment timeout (typically 1 hour).",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract cart items and craft recovery messaging",
        280,
        "You are an e-commerce recovery copywriter. Given abandoned cart details (items, total, customer history), generate recovery_angle (product_focus, social_proof, urgency, fomo), subject_line, product_highlight (the most compelling item description), and incentive_recommendation (none, free_shipping, or percentage_off based on cart value).",
        "Cart items: {cart_items}. Cart total: ${cart_total}. Customer: {first_name}. Previous purchases: {purchase_count}. Cart created: {cart_created_at}. Analyze and generate recovery messaging.",
        [
          { field: "recovery_angle", as: "recovery_angle", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          {
            field: "product_highlight",
            as: "product_highlight",
            type: "string",
          },
          {
            field: "incentive_recommendation",
            as: "incentive_type",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch cart details with product images and customer data",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{dc}.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/carts/{cart_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "customer.email_address",
                customName: "customer_email",
                type: "string",
              },
              { name: "order_total", customName: "cart_total", type: "number" },
              { name: "lines", customName: "cart_lines", type: "object" },
              {
                name: "checkout_url",
                customName: "checkout_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is cart value above $75 (high-value recovery)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "cart_total", operator: "greater_than", value: "75" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: First cart reminder with product images and direct checkout link",
        1000,
        "{customer_email}",
        "{subject_line}",
        "Hi {first_name},\\n\\nLooks like you left some great items in your cart!\\n\\n{product_highlight}\\n\\nYour cart summary:\\n{cart_items_formatted}\\n\\n**Cart total: ${cart_total}**\\n\\nYour items are saved, but they are selling fast. Complete your purchase now:\\n\\n{checkout_url}\\n\\nFree shipping on orders over $50!\\n\\nQuestions? Reply to this email and we will help.\\n\\n— The Team",
      ),
      n("6", "delayAction", "Wait 18 hours before second reminder", 1240, {
        delayDetails: { duration: 18, unit: "hours" },
      }),
      n("7", "condition", "Has customer completed purchase?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "purchase_completed", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Second reminder with social proof and limited-time incentive",
        1720,
        "{customer_email}",
        "Still thinking about it, {first_name}? Here's what others are saying",
        'Hi {first_name},\\n\\nYour cart is still waiting — and we did not want you to miss out.\\n\\nHere is what other customers say about the items in your cart:\\n\\n⭐⭐⭐⭐⭐ "{review_1}" — {reviewer_1}\\n⭐⭐⭐⭐⭐ "{review_2}" — {reviewer_2}\\n\\nAs a thank you for coming back, enjoy free shipping on your order — no minimum required.\\n\\n**Use code: FREESHIP** at checkout\\n\\nComplete your order: {checkout_url}\\n\\nThis offer expires in 24 hours. Don\'t let these items sell out!\\n\\n— The Team',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Abandoned cart recovery series for Mailchimp e-commerce. Triggers on cart abandonment, uses AI to determine the best recovery angle (product focus, social proof, urgency) and incentive recommendation based on cart value, fetches full cart details with product images and checkout URL, sends a first reminder email with product highlights and direct checkout link, waits 18 hours, then sends a second reminder with customer reviews and a free shipping incentive code for non-converters.",
  },

  // ─── 161. Product Retargeting Journey (Mailchimp, Pattern C) ──────────

  "Product Retargeting Journey": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber viewed product page without adding to cart",
        "webhook_app",
        {
          source: "mailchimp",
          event: "product.viewed",
          howItStarts:
            "Fires when a Mailchimp-tracked subscriber views a specific product page but does not add it to their cart within the session, indicating browse interest without purchase intent.",
          setup:
            "Connect Mailchimp with your e-commerce store and enable product browse tracking via the Mailchimp site script. Configure the browse abandonment window (typically 30 minutes post-session).",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract viewed product details and generate retargeting content",
        280,
        "You are a product retargeting specialist. Given a viewed product (name, category, price, description) and customer browse history, generate retargeting_hook (one compelling reason to buy), similar_products_query (search query for recommendations), and email_subject (curiosity-driven subject line).",
        "Viewed product: {product_name}. Category: {product_category}. Price: ${product_price}. Description: {product_description}. Customer: {first_name}. Browse history: {browse_history}. Generate retargeting content.",
        [
          { field: "retargeting_hook", as: "retargeting_hook", type: "string" },
          {
            field: "similar_products_query",
            as: "similar_query",
            type: "string",
          },
          { field: "email_subject", as: "email_subject", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber profile and e-commerce activity",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{dc}.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}/activity",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "activity",
                customName: "recent_activity",
                type: "object",
              },
              {
                name: "total_items",
                customName: "activity_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is this a returning customer (previous purchase history)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "purchase_count", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Product retarget with viewed item, details, and shop link",
        1000,
        "{subscriber_email}",
        "{email_subject}",
        "Hi {first_name},\\n\\nWe noticed you were checking out {product_name} — great taste!\\n\\n{retargeting_hook}\\n\\n**{product_name}** — ${product_price}\\n{product_short_description}\\n\\n✅ In stock and ready to ship\\n🚚 Free shipping on orders over $50\\n↩️ 30-day hassle-free returns\\n\\nSee full details: {product_link}\\n\\nCustomers who viewed this also loved:\\n• {similar_product_1} — ${similar_price_1}\\n• {similar_product_2} — ${similar_price_2}\\n\\n— The Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait 1 day before follow-up recommendations",
        1240,
        {
          delayDetails: { duration: 1, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Has subscriber added product to cart or purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "cart_added", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Category recommendations with bestsellers and new arrivals",
        1720,
        "{subscriber_email}",
        "More {product_category} picks you might love, {first_name}",
        "Hi {first_name},\\n\\nSince you showed interest in {product_category}, here are some of our top picks in that category:\\n\\n🏆 **Bestsellers**\\n• {bestseller_1} — ${bestseller_1_price} (⭐ {bestseller_1_rating})\\n• {bestseller_2} — ${bestseller_2_price} (⭐ {bestseller_2_rating})\\n\\n🆕 **New Arrivals**\\n• {new_arrival_1} — ${new_arrival_1_price}\\n• {new_arrival_2} — ${new_arrival_2_price}\\n\\nBrowse all {product_category}: {category_link}\\n\\nFree shipping on orders over $50!\\n\\n— The Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Product retargeting journey for Mailchimp browse abandonment. Triggers when a subscriber views a product page without adding to cart, uses AI to generate a compelling retargeting hook and curiosity-driven subject line, fetches subscriber activity history, sends a retargeting email featuring the viewed product with details, stock status, and similar product suggestions, waits 1 day, then sends category-level recommendations with bestsellers and new arrivals for subscribers who still have not engaged.",
  },

  // ─── 162. Browse Retargeting Journey (Mailchimp, Pattern C) ───────────

  "Browse Retargeting Journey": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber browsed category pages without viewing specific product",
        "webhook_app",
        {
          source: "mailchimp",
          event: "category.browsed",
          howItStarts:
            "Fires when a Mailchimp-tracked subscriber browses category or collection pages without drilling into a specific product, indicating broad interest in a category without specific purchase intent.",
          setup:
            "Connect Mailchimp with your e-commerce store and enable category-level browse tracking. Configure the browse event to fire when a subscriber views 2+ category pages in a session without a product click.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract browsed categories and recommend featured products",
        280,
        "You are a merchandising strategist. Given the categories a subscriber browsed (with timestamps and dwell time), determine primary_interest_category, browsing_intent (casual, comparison_shopping, gift_seeking, restocking), recommended_products_angle (trending, editor_picks, best_value, new_in), and email_subject (engaging subject line matching the intent).",
        "Browsed categories: {browsed_categories}. Session duration: {session_duration}. Subscriber: {first_name}. Previous purchases: {purchase_history}. Determine browsing intent and product recommendation angle.",
        [
          {
            field: "primary_interest_category",
            as: "primary_category",
            type: "string",
          },
          { field: "browsing_intent", as: "browsing_intent", type: "string" },
          {
            field: "recommended_products_angle",
            as: "products_angle",
            type: "string",
          },
          { field: "email_subject", as: "email_subject", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber profile and purchase history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{dc}.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/customers/{customer_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "email_address",
                customName: "subscriber_email",
                type: "string",
              },
              { name: "first_name", customName: "first_name", type: "string" },
              {
                name: "orders_count",
                customName: "orders_count",
                type: "number",
              },
              {
                name: "total_spent",
                customName: "total_spent",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is subscriber a returning customer (orders > 0)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "orders_count", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Category highlights with curated collection and editorial picks",
        1000,
        "{subscriber_email}",
        "{email_subject}",
        "Hi {first_name},\\n\\nWe saw you exploring our {primary_category} collection — here is a curated selection just for you:\\n\\n✨ **Editor's Picks in {primary_category}**\\n\\n• {pick_1_name} — ${pick_1_price} | {pick_1_tagline}\\n• {pick_2_name} — ${pick_2_price} | {pick_2_tagline}\\n• {pick_3_name} — ${pick_3_price} | {pick_3_tagline}\\n\\n🔥 **Trending Now**\\n• {trending_1_name} — ${trending_1_price}\\n• {trending_2_name} — ${trending_2_price}\\n\\nBrowse the full {primary_category} collection: {collection_link}\\n\\nFree shipping on orders over $50!\\n\\n— The Team",
      ),
      n("6", "delayAction", "Wait 1 day before broader recommendations", 1240, {
        delayDetails: { duration: 1, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Has subscriber viewed a specific product or added to cart?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "product_viewed_or_carted",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Broader collection picks with style guide and customer favorites",
        1720,
        "{subscriber_email}",
        "{first_name}, explore more collections you might love",
        'Hi {first_name},\\n\\nSince you are interested in {primary_category}, you might also love these related collections:\\n\\n📦 **{related_collection_1}** — {collection_1_description}\\nSee collection: {collection_1_link}\\n\\n📦 **{related_collection_2}** — {collection_2_description}\\nSee collection: {collection_2_link}\\n\\n💡 **Style Guide**: How to pair items from {primary_category} for the perfect look: {style_guide_link}\\n\\n⭐ **Customer Favorites** — The #1 bestseller in {primary_category}:\\n{bestseller_name} — ${bestseller_price} | "{bestseller_review}"\\n\\nShop all collections: {shop_link}\\n\\n— The Team',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Browse retargeting journey for Mailchimp category browsers. Triggers when a subscriber browses category pages without clicking into specific products, uses AI to determine browsing intent (casual, comparison shopping, gift seeking) and the best product recommendation angle, fetches subscriber purchase history, sends a curated email with editor's picks and trending items from the browsed category, waits 1 day, then sends broader collection recommendations with a style guide and customer favorites for subscribers who still have not drilled into a product.",
  },

  // ─── 163. Checkout Reminder Journey (Mailchimp, Pattern B) ────────────

  "Checkout Reminder Journey": {
    nodes: [
      trigger(
        "Mailchimp webhook: Checkout started but not completed",
        "webhook_app",
        {
          source: "mailchimp",
          event: "checkout.abandoned",
          howItStarts:
            "Fires when a Mailchimp-connected store detects that a subscriber started the checkout process (entered email/shipping) but did not complete payment, indicating high purchase intent that stalled at the final step.",
          setup:
            "Connect Mailchimp with your e-commerce store and enable checkout tracking. The webhook fires when checkout is not completed within the configured timeout (typically 30-60 minutes).",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Analyze checkout abandonment and determine recovery strategy",
        280,
        "You are a checkout optimization specialist. Given checkout details (items, subtotal, shipping cost, step reached), determine likely_abandon_reason (shipping_cost, payment_friction, price_hesitation, distraction, security_concern), recovery_strategy (reassurance, free_shipping_offer, urgency, support_offer), and email_subject (action-oriented subject line).",
        "Checkout items: {checkout_items}. Subtotal: ${subtotal}. Shipping: ${shipping_cost}. Step reached: {last_checkout_step}. Customer: {first_name}. Previous orders: {order_count}. Analyze abandon reason and recommend recovery strategy.",
        [
          {
            field: "likely_abandon_reason",
            as: "abandon_reason",
            type: "string",
          },
          {
            field: "recovery_strategy",
            as: "recovery_strategy",
            type: "string",
          },
          { field: "email_subject", as: "email_subject", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch checkout details with line items and customer data",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{dc}.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/carts/{cart_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
            ],
            body: [],
            variables: [
              {
                name: "customer.email_address",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "order_total",
                customName: "checkout_total",
                type: "number",
              },
              { name: "lines", customName: "checkout_lines", type: "object" },
              {
                name: "checkout_url",
                customName: "checkout_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Was shipping cost likely the abandon reason?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "abandon_reason",
              operator: "equals",
              value: "shipping_cost",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Checkout recovery with order summary and resume link",
        1000,
        "{customer_email}",
        "{email_subject}",
        "Hi {first_name},\\n\\nYou were so close to completing your order! Your checkout is saved and ready to pick up right where you left off.\\n\\n**Your order summary:**\\n{checkout_items_formatted}\\n\\nSubtotal: ${subtotal}\\nShipping: ${shipping_cost}\\n**Total: ${checkout_total}**\\n\\n🔒 Secure checkout — your information is protected\\n↩️ 30-day easy returns\\n📞 Need help? Our team is here: {support_link}\\n\\nResume your checkout: {checkout_url}\\n\\nYour saved checkout expires in 48 hours.\\n\\n— The Team",
      ),
      n("6", "delayAction", "Wait 12 hours before urgency follow-up", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      n("7", "condition", "Has customer completed the checkout?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "checkout_completed", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Urgency follow-up with limited stock warning and support offer",
        1720,
        "{customer_email}",
        "Your items may sell out soon, {first_name} — complete your order now",
        "Hi {first_name},\\n\\nJust a heads-up — the items in your checkout are moving fast and we can not guarantee availability much longer.\\n\\n{checkout_items_formatted}\\n\\n**Total: ${checkout_total}**\\n\\n⚠️ Limited stock remaining on {low_stock_item}\\n\\nTo make it easier, here is a little incentive — use code **FINISH10** for 10% off your order.\\n\\nComplete your purchase now: {checkout_url}\\n\\nHaving trouble with checkout? Our support team can help you complete your order over the phone: {phone_number}\\n\\nThis offer and your saved checkout expire at midnight tonight.\\n\\n— The Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Checkout reminder journey for Mailchimp e-commerce. Triggers when a subscriber starts checkout but does not complete payment, uses AI to analyze the likely abandonment reason (shipping cost, payment friction, price hesitation) and determine the optimal recovery strategy, fetches full checkout details with line items, sends a first recovery email with a complete order summary and secure resume link, waits 12 hours, then sends an urgency follow-up with limited stock warnings and a 10% discount code (FINISH10) for customers who still have not completed their purchase.",
  },
};
